import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import { showElement, hideElement } from "./utils";

export default class extends Controller {
    static values = {
        url: String,
        pageType: String
    };
    static targets = [
        "input",
        "loader",
        "resultsContainer",
        "addressResults",
    ];

    performSearchAndShowRelevantProfiles(e) {
        e.preventDefault();

        const query = this.inputTarget.value.trim();
        const controller = new AbortController();
        const signal = controller.signal;

        if (!query) {
            this.inputTarget.focus();
            return;
        }

        if (this.prevController) {
            this.prevController.abort();
        }

        hideElement(this.resultsContainerTarget);
        showElement(this.loaderTarget);

        const url = `/website-api/area/places/?q=${query}`;

        this.prevController = controller;

        const debouncedFetch = debounce((url, signal ) => {
            return this.fetchSearchResults(url, signal)
                .then(places => {

                    const url = `/website-api/landing/relevant-users/?lat=${places[0]['lat']}&lng=${places[0]['lng']}&page_type=${this.pageTypeValue}`;
                    const controller = new AbortController();
                    this.prevController = controller;
                    const signal = controller.signal;

                    let debounceFetch = debounce(this.fetchRelevantProfiles, 400);

                    debounceFetch(url, signal, this);
                });
        }, 400);

        debouncedFetch(url, signal, this);
    }

    performSearch(e) {
        e.preventDefault();

        const query = this.inputTarget.value.trim();
        const controller = new AbortController();
        const signal = controller.signal;

        if (!query) {
            this.inputTarget.focus();
            return;
        }

        if (this.prevController) {
            this.prevController.abort();
        }

        const url = `/website-api/area/places/?q=${query}`;

        this.prevController = controller;

        showElement(this.loaderTarget);

        const debouncedFetch = debounce((url, signal) => {
            return this.fetchSearchResults(url, signal)
                .then(places => {
                    hideElement(this.loaderTarget);
                    this.showResultsWithData(places);
                });
        }, 400);

        debouncedFetch(url, signal, this);
    }

    loadRelevantProfiles(e) {
        e.preventDefault();

        hideElement(this.resultsContainerTarget);

        const url = `/website-api/landing/relevant-users/?lat=${e.target.dataset.lat}&lng=${e.target.dataset.lng}&page_type=${this.pageTypeValue}`;
        const controller = new AbortController();
        this.prevController = controller;
        const signal = controller.signal;

        showElement(this.loaderTarget);

        let debounceFetch = debounce(this.fetchRelevantProfiles, 400);

        debounceFetch(url, signal, this);
    }

    hideResults(e) {
        if (e.relatedTarget && e.relatedTarget.dataset.type === "href-action") {
            return;
        }

        hideElement(this.resultsContainerTarget);
    }

    showResults(e) {
        if (!this.inputTarget.value.trim()) {
            return;
        }

        showElement(this.resultsContainerTarget);
    }

    showResultsWithData(places) {
        showElement(this.resultsContainerTarget);

        let linkCollections = "";

        for (const place of places) {
            let url = `${this.urlValue}?query=${place.title}&lat=${place.lat}&lng=${place.lng}&location_search=address`;

            /* Add tabindex to a element to make relatedTarget work in Safari */
            linkCollections += `<a href="${url}" data-type="href-action" tabindex="0" data-action="landing-autocomplete#loadRelevantProfiles" data-lat="${place.lat}" data-lng="${place.lng}">${place.title}</a>`;
        }

        this.addressResultsTarget.innerHTML = linkCollections;
    }

    fetchSearchResults(url, signal, self) {
        return fetch(url, { signal })
            .then((response) => {
                if (response.status === 401) {
                    window.location.href = "/";
                }
                if (response.status === 429) {
                    hideElement(self.loaderTarget);
                    alert(
                        "We're sorry, but you have sent too many requests to us recently. Please try again later.",
                    );

                    throw new Error("Too many requests");
                }
                if (response.status === 403) {
                    hideElement(self.loaderTarget);

                    return response
                        .json()
                        .then((jsonResponse) => {
                            const errorMessage =
                                jsonResponse.metadata?.error_message ||
                                "VPN Detected (403). Please switch off your VPN or Proxy. Just like at a daycare, we need to see who's at the door!";
                            alert(errorMessage);
                            throw new Error("VPN Detected (403)");
                        })
                        .catch((parseError) => {
                            // If JSON parsing fails, use default message
                            alert(
                                "VPN Detected (403). Please switch off your VPN or Proxy. Just like at a daycare, we need to see who's at the door!",
                            );
                            throw new Error("VPN Detected (403)");
                        });
                }

                if (response.status >= 500) {
                    hideElement(self.loaderTarget);
                    alert("We ran into a problem. Please try again.");

                    throw new Error("500 problem.");
                }

                return response.json();
            })
            .then((jsonResponse) => {
                return !jsonResponse.data.length ? [] : jsonResponse.data;
            })
            .catch((e) => {
                return [];
            });
    }

    fetchRelevantProfiles(url, signal, self) {
        fetch(url, { signal })
            .then((response) => {
                if (response.status === 401) {
                    window.location.href = "/";
                }
                if (response.status === 429) {
                    hideElement(self.loaderTarget);
                    alert(
                        "We're sorry, but you have sent too many requests to us recently. Please try again later.",
                    );

                    throw new Error("Too many requests");
                }
                if (response.status === 403) {
                    hideElement(self.loaderTarget);

                    return response
                        .json()
                        .then((jsonResponse) => {
                            const errorMessage =
                                jsonResponse.metadata?.error_message ||
                                "VPN Detected (403). Please switch off your VPN or Proxy. Just like at a daycare, we need to see who's at the door!";
                            alert(errorMessage);
                            throw new Error("VPN Detected (403)");
                        })
                        .catch((parseError) => {
                            // If JSON parsing fails, use default message
                            alert(
                                "VPN Detected (403). Please switch off your VPN or Proxy. Just like at a daycare, we need to see who's at the door!",
                            );
                            throw new Error("VPN Detected (403)");
                        });
                }

                if (response.status >= 500) {
                    hideElement(self.loaderTarget);
                    alert("We ran into a problem. Please try again.");

                    throw new Error("500 problem.");
                }

                return response.text();
            })
            .then((htmlString) => {
                hideElement(self.loaderTarget);

                const relevantUsers = document.querySelector('.relevant-users');

                relevantUsers.innerHTML = htmlString;
            })
            .catch((e) => {});
    }
}
